import React, {useEffect, useState} from "react";
import "../../property.css";

// MUI Components
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Alert,
    IconButton,
    Tooltip,
    Chip,
    InputAdornment
} from "@mui/material";

// MUI Data Grid
import {DataGrid} from "@mui/x-data-grid";
import {styled} from "@mui/system";

// MUI Icons
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UpdateIcon from "@mui/icons-material/Update";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import AirbnbIcon from "@mui/icons-material/Bed";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

// MUI Button with loading state
import LoadingButton from "@mui/lab/LoadingButton";

// Other libraries
import CryptoJS from "crypto-js";
import {useConfirm} from "material-ui-confirm";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import {ApiDetails, KEY} from "../../../../Constants";
import {Water} from "@mui/icons-material";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';
import { getAuthToken } from '../../../../services/api/client';


// Styled components for DataGrid
const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    "& .MuiDataGrid-root": {
        border: "none",
        borderRadius: 12,
        minHeight: "400px"
    },
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "0.95rem",
        fontWeight: 700,
        backgroundColor: "#2C5283",
        color: "#fff",
        borderRadius: "8px 8px 0 0"
    },
    "& .MuiDataGrid-virtualScroller": {
        backgroundColor: "#fff"
    },
    "& .MuiDataGrid-footerContainer": {
        backgroundColor: "#f5f5f5",
        borderRadius: "0 0 8px 8px",
        borderTop: "none"
    },
    "& .MuiDataGrid-row": {
        fontSize: "0.9rem"
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#f9fafb"
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#edf2f9"
    },
    "& .MuiDataGrid-cell": {
        borderBottom: "1px solid #f0f0f0",
        padding: "12px 16px"
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
        outline: "none"
    },
    "& .vacant-cell": {
        color: "#4caf50",
        fontWeight: "bold"
    },
    "& .occupied-cell": {
        color: "#f57c00",
        fontWeight: "bold"
    },
    "& .short-term-row": {
        borderLeft: "4px solid #ff5722"
    },
    "& .long-term-row": {
        borderLeft: "4px solid #2196f3"
    }
}));

// Form Section
const FormCard = styled(Card)(({theme}) => ({
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.05)",
    borderRadius: "12px",
    marginBottom: "24px",
    overflow: "visible"
}));

// CardHeader styled
const StyledCardHeader = styled(CardHeader)(({theme}) => ({
    backgroundColor: "#f5f7fa",
    borderBottom: "1px solid #eaecef",
    "& .MuiCardHeader-title": {
        fontSize: "1.1rem",
        fontWeight: 600,
        color: "#2C5283"
    }
}));

// TextField styled
const StyledTextField = styled(TextField)(({theme}) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2C5283"
            }
        },
        "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2C5283",
                borderWidth: "2px"
            }
        }
    },
    "& .MuiInputLabel-root": {
        color: "#555",
        "&.Mui-focused": {
            color: "#2C5283"
        }
    }
}));

export default function EnhancedUnitsManagement(props) {
    const {propertyDetails, tenants, setBillTenantsModal, billTenantsModal} = props;
    const [propertyId, setPropertyId] = useState(propertyDetails.property_id);
    const confirm = useConfirm();

    // State management
    let [meterReadings, setMeterReadings] = useState([]);
    let [electricityMeterReadings, setElectricityMeterReadings] = useState([]);
    const [warning, setWarning] = useState("");
    const [property, setProperty] = useState({expected_income: 0, agency_id: 0});

    // Unit form state
    const [unitName, setUnitName] = useState("");
    const [unitMeterReading, setUnitMeterReading] = useState("");
    const [electricityUnitMeterReading, setElectricityUnitMeterReading] = useState("");
    const [unitType, setUnitType] = useState("long_term");
    const [nightlyRate, setNightlyRate] = useState("");
    const [cleaningFee, setCleaningFee] = useState("");
    const [minimumStay, setMinimumStay] = useState("1");
    const [maximumStay, setMaximumStay] = useState("");
    // Airbnb listing ID is auto-generated, not user-editable
    const [houseRules, setHouseRules] = useState("");

    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingUnitId, setEditingUnitId] = useState(null);

    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState("");
    const [billLoading, setBillLoading] = useState(false);
    const [addUnitLoading, setAddUnitLoading] = useState(false);
    const [updateUnitsLoading, setUpdateUnitsLoading] = useState(false);
    const [formError, setFormError] = useState("");

    // Decrypt local storage data
    let myMapDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.getItem("myMap"), KEY);
    let portfolioDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.portfolioDetails, KEY);
    let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
    portfolioDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // UUID generation function
    const generateUUID = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback UUID generation
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Load units on component mount
    useEffect(() => {
        getUnits();
    }, []);

    // API functions
    const getUnits = () => {
        const token = getAuthToken();
        console.log("Access token : " + token);

        fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({property_id: propertyId}),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setUnits(data);
                    showSnackbar({
                        title: "Success!",
                        message: "Units retrieved successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to retrieve units. Please try again.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const addUnit = () => {
        // Form validation
        if (unitName.trim() === "") {
            setFormError("Unit name is required");
            return;
        }

        if (unitMeterReading.trim() === "") {
            setFormError("Water meter reading is required");
            return;
        }

        if (electricityUnitMeterReading.trim() === "") {
            setFormError("Electricity meter reading is required");
            return;
        }

        // Validate short-term rental specific fields
        if (unitType === "short_term") {
            if (!nightlyRate || parseFloat(nightlyRate) <= 0) {
                setFormError("Nightly rate is required for short-term rentals");
                return;
            }
        }

        setFormError("");
        setAddUnitLoading(true);

        const unitData = {
            property_id: propertyId,
            unit: unitName,
            state: "VACANT",
            meter_reading: unitMeterReading,
            electricity_meter_reading: electricityUnitMeterReading,
            unit_type: unitType,
            nightly_rate: unitType === "short_term" ? parseFloat(nightlyRate) || null : null,
            cleaning_fee: unitType === "short_term" ? parseFloat(cleaningFee) || null : null,
            minimum_stay_nights: unitType === "short_term" ? parseInt(minimumStay) || 1 : null,
            maximum_stay_nights: unitType === "short_term" && maximumStay ? parseInt(maximumStay) : null,
            airbnb_listing_id: unitType === "short_term" ? (isEditMode ? undefined : generateUUID()) : null,
            house_rules: unitType === "short_term" ? (houseRules || null) : null
        };

        const apiEndpoint = isEditMode
            ? "pegasus/visionary/property/update/unit"
            : "pegasus/visionary/property/add/unit";

        if (isEditMode) {
            unitData.id = editingUnitId;
        }

        fetch(ApiDetails + apiEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken()
            },
            body: JSON.stringify(unitData),
        })
            .then(async (response) => {
                let data = await response.json();
                setAddUnitLoading(false);

                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setUnits(data);
                    getUnits();
                    // Clear form fields after successful submission
                    clearForm();

                    showSnackbar({
                        title: "Success!",
                        message: isEditMode ? "Unit Updated Successfully" : "Unit Added Successfully",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else if (response.status === 500) {
                    showSnackbar({
                        title: "Error!",
                        message: `Failed to ${isEditMode ? 'update' : 'add'} unit. Please try again.`,
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                setAddUnitLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const editUnit = (unit) => {
        setIsEditMode(true);
        setEditingUnitId(unit.id);
        setUnitName(unit.unit);
        setUnitMeterReading(unit.meter_reading?.toString() || "");
        setElectricityUnitMeterReading(unit.electricity_meter_reading?.toString() || "");
        setUnitType(unit.unit_type || "long_term");
        setNightlyRate(unit.nightly_rate?.toString() || "");
        setCleaningFee(unit.cleaning_fee?.toString() || "");
        setMinimumStay(unit.minimum_stay_nights?.toString() || "1");
        setMaximumStay(unit.maximum_stay_nights?.toString() || "");
        setHouseRules(unit.house_rules || "");

        // Scroll to form
        document.getElementById('unit-form').scrollIntoView({behavior: 'smooth'});
    };

    const clearForm = () => {
        setUnitName("");
        setUnitMeterReading("");
        setElectricityUnitMeterReading("");
        setUnitType("long_term");
        setNightlyRate("");
        setCleaningFee("");
        setMinimumStay("1");
        setMaximumStay("");
        setHouseRules("");
        setIsEditMode(false);
        setEditingUnitId(null);
        setFormError("");
    };

    const cancelEdit = () => {
        clearForm();
    };

    const DeleteUnit = (unitId) => {
        fetch(ApiDetails + "pegasus/visionary/property/delete/unit", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken()
            },
            body: JSON.stringify({
                property_id: propertyId,
                id: unitId,
            }),
        })
            .then(async (response) => {
                let data = await response.json();

                if (response.status === 403) {
                    localStorage.clear();
                    window.location.href = "/";

                } else if (response.status === 200) {
                    setUnits(data);
                    getUnits();
                    showSnackbar({
                        title: "Success!",
                        message: "Unit deleted successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else if (response.status === 409) {
                    showSnackbar({
                        title: "Error!",
                        message: "Unit is currently occupied.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to delete unit. Please try again.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const updateUnits = () => {
        if (meterReadings.length === 0 && electricityMeterReadings.length === 0) {
            showSnackbar({
                title: "Warning!",
                message: "No readings to update. Please enter at least one new reading.",
                type: "warning",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setUpdateUnitsLoading(true);
        let finalChanges = [];

        if (meterReadings.length >= electricityMeterReadings.length) {
            meterReadings.forEach((reading) => {
                electricityMeterReadings.forEach((readings2) => {
                    if (reading.unit === readings2.unit) {
                        finalChanges = [...finalChanges, {
                            unit: reading.unit,
                            meter_reading: reading.meter_reading,
                            electricity_meter_reading: readings2.electricity_meter_reading,
                        }];
                        meterReadings = meterReadings.filter((r) => r.unit !== readings2.unit);
                        electricityMeterReadings = electricityMeterReadings.filter((r) => r.unit !== readings2.unit);
                    }
                });
            });
        } else {
            electricityMeterReadings.forEach((reading) => {
                meterReadings.forEach((readings2) => {
                    if (reading.unit === readings2.unit) {
                        finalChanges = [...finalChanges, {
                            unit: reading.unit,
                            meter_reading: readings2.meter_reading,
                            electricity_meter_reading: reading.electricity_meter_reading,
                        }];
                        meterReadings = meterReadings.filter((r) => r.unit !== readings2.unit);
                        electricityMeterReadings = electricityMeterReadings.filter((r) => r.unit !== readings2.unit);
                    }
                });
            });
        }

        let finalMeterReadings = meterReadings.map(reading => ({
            ...reading,
            electricity_meter_reading: 0
        }));

        let finalElectricityMeterReadings = electricityMeterReadings.map(reading => ({
            ...reading,
            meter_reading: 0
        }));

        finalChanges = [...finalChanges, ...finalMeterReadings, ...finalElectricityMeterReadings];

        fetch(ApiDetails + "pegasus/visionary/property/update/Units", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken()
            },
            body: JSON.stringify({
                property_id: propertyId,
                units: finalChanges,
            }),
        })
            .then(async (response) => {
                setUpdateUnitsLoading(false);
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    getUnits();
                    setMeterReadings([]);
                    setElectricityMeterReadings([]);

                    showSnackbar({
                        title: "Success!",
                        message: "Units updated successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to update units. Please try again.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                setUpdateUnitsLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const downloadUnitReport = () => {
        if (!selectedUnit) {
            showSnackbar({
                title: "Warning!",
                message: "Please select a unit first.",
                type: "warning",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setBillLoading(true);
        let fileName;

        fetch(ApiDetails + "pegasus/visionary/property/unit/report", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken()
            },
            body: JSON.stringify({
                property_id: propertyId,
                id: selectedUnit,
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setBillLoading(false);

                    showSnackbar({
                        title: "Success!",
                        message: "Download Will Start Shortly",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    for (let pair of response.headers.entries()) {
                        if (pair[0] === "content-disposition") {
                            fileName = pair[1];
                        }
                    }

                    let blob = await response.blob();
                    let href = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                } else {
                    setBillLoading(false);
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to generate report. Please try again.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                setBillLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    // Helper function to get unit type chip
    const getUnitTypeChip = (type) => {
        const configs = {
            long_term: {label: "Long Term", color: "primary", icon: <HomeWorkIcon/>},
            short_term: {label: "Short Term", color: "error", icon: <AirbnbIcon/>}
        };

        const config = configs[type] || configs.long_term;

        return (
            <Chip
                icon={config.icon}
                label={config.label}
                color={config.color}
                size="small"
                variant="outlined"
            />
        );
    };

    // Enhanced DataGrid column definitions
    const unitsColumns = [
        {
            field: "unit",
            headerName: "Unit",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <HomeWorkIcon color="primary" fontSize="small"/>
                    <Typography variant="body2" fontWeight="medium">
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: "unit_type",
            headerName: "Type",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => getUnitTypeChip(params.value || 'long_term')
        },
        {
            field: "state",
            headerName: "Status",
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: '16px',
                    backgroundColor: params.value === 'VACANT' ? '#e8f5e9' : '#fff3e0',
                    color: params.value === 'VACANT' ? '#2e7d32' : '#e65100',
                    display: 'inline-flex',
                    alignItems: 'center'
                }}>
                    <Typography variant="body2" fontWeight="bold">
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: "nightly_rate",
            headerName: "Nightly Rate",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                if (!params.value || params.row.unit_type === 'long_term') {
                    return <Typography variant="body2" color="textSecondary">N/A</Typography>;
                }
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <AttachMoneyIcon color="success" fontSize="small"/>
                        <Typography variant="body2">{params.value}</Typography>
                    </Box>
                );
            }
        },
        {
            field: "minimum_stay_nights",
            headerName: "Min Stay",
            minWidth: 90,
            flex: 1,
            renderCell: (params) => {
                if (!params.value || params.row.unit_type === 'long_term') {
                    return <Typography variant="body2" color="textSecondary">N/A</Typography>;
                }
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <NightsStayIcon color="info" fontSize="small"/>
                        <Typography variant="body2">{params.value} nights</Typography>
                    </Box>
                );
            }
        },
        /*{
            field: "airbnb_listing_id",
            headerName: "Airbnb ID",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                if (!params.value || params.row.unit_type === 'long_term') {
                    return <Typography variant="body2" color="textSecondary">N/A</Typography>;
                }
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <AirbnbIcon color="error" fontSize="small"/>
                        <Tooltip title={params.value}>
                            <Typography variant="body2" sx={{fontFamily: 'monospace', fontSize: '0.75rem'}}>
                                {params.value.substring(0, 8)}...
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            }
        },*/
        {
            field: "meter_reading",
            headerName: "Water Meter",
            minWidth: 110,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Water color="info" fontSize="small"/>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: "electricity_meter_reading",
            headerName: "Electric Meter",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <ElectricBoltIcon color="warning" fontSize="small"/>
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: "water",
            headerName: "Update Water",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <StyledTextField
                    type="number"
                    size="small"
                    variant="outlined"
                    placeholder="Enter reading"
                    InputProps={{
                        startAdornment: <Water color="info" fontSize="small" sx={{mr: 1}}/>
                    }}
                    onChange={(e) => {
                        const newReading = parseFloat(e.target.value);
                        if (isNaN(newReading)) return;

                        const filteredReadings = meterReadings.filter(
                            (reading) => reading.unit !== params.row.unit
                        );

                        setMeterReadings([
                            ...filteredReadings,
                            {
                                unit: params.row.unit,
                                meter_reading: newReading,
                            },
                        ]);
                    }}
                />
            ),
        },
        {
            field: "electricity",
            headerName: "Update Electric",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <StyledTextField
                    type="number"
                    size="small"
                    variant="outlined"
                    placeholder="Enter reading"
                    InputProps={{
                        startAdornment: <ElectricBoltIcon color="warning" fontSize="small" sx={{mr: 1}}/>
                    }}
                    onChange={(e) => {
                        const newReading = parseFloat(e.target.value);
                        if (isNaN(newReading)) return;

                        const filteredReadings = electricityMeterReadings.filter(
                            (reading) => reading.unit !== params.row.unit
                        );

                        setElectricityMeterReadings([
                            ...filteredReadings,
                            {
                                unit: params.row.unit,
                                electricity_meter_reading: newReading,
                            },
                        ]);
                    }}
                />
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 120,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{display: 'flex', gap: 0.5}}>
                    <Tooltip title="Edit unit">
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => editUnit(params.row)}
                        >
                            <EditIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete unit">
                        <IconButton
                            color="error"
                            size="small"
                            onClick={async () => {
                                let deleteUnit = false;

                                await confirm({
                                    title: "Delete Unit",
                                    description: `Are you sure you want to delete unit ${params.row.unit}?`,
                                    confirmationText: "Delete",
                                    cancellationText: "Cancel",
                                    confirmationButtonProps: {color: "error"}
                                })
                                    .then(() => {
                                        deleteUnit = true;
                                    })
                                    .catch(() => {
                                        deleteUnit = false;
                                    });

                                if (deleteUnit) {
                                    DeleteUnit(params.row.id);
                                }
                            }}
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    if (propertyDetails.property_id === "") return (
        <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "100vh"}}>
            <Typography variant="h6">Loading property details...</Typography>
        </Box>
    );

    return (
        <Container maxWidth="xl" sx={{py: 4}}>
            {/* Report Generation Section */}
            <FormCard>
                <StyledCardHeader
                    title={
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <DownloadIcon/>
                            <span>Generate Unit Report</span>
                        </Box>
                    }
                />
                <CardContent>
                    <Grid container spacing={3} alignItems="center" justifyContent="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="unit-select-label">Select Unit</InputLabel>
                                <Select
                                    labelId="unit-select-label"
                                    id="unit-select"
                                    value={selectedUnit}
                                    onChange={(e) => setSelectedUnit(e.target.value)}
                                    label="Select Unit"
                                    disabled={!units.length}
                                >
                                    {units.length ? (
                                        units.map((unit, index) => (
                                            <MenuItem key={index} value={unit.id}>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    {getUnitTypeChip(unit.unit_type || 'long_term')}
                                                    <span>{unit.unit}</span>
                                                </Box>
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="">
                                            <em>No units available</em>
                                        </MenuItem>
                                    )}
                                </Select>
                                <FormHelperText>Select a unit to generate report</FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} sx={{display: "flex", justifyContent: "center"}}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<DownloadIcon/>}
                                loading={billLoading}
                                loadingPosition="start"
                                onClick={downloadUnitReport}
                                disabled={!selectedUnit || !!warning}
                                sx={{px: 4, borderRadius: 2}}
                            >
                                Generate Report
                            </LoadingButton>
                        </Grid>
                    </Grid>

                    {warning && (
                        <Alert severity="warning" sx={{mt: 2}}>
                            {warning}
                        </Alert>
                    )}
                </CardContent>
            </FormCard>

            {/* Add/Edit Unit Section */}
            <FormCard id="unit-form">
                <StyledCardHeader
                    title={
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            {isEditMode ? <EditIcon/> : <AddCircleIcon/>}
                            <span>{isEditMode ? 'Edit Unit' : 'Add New Unit'}</span>
                            {isEditMode && (
                                <Chip
                                    label="Edit Mode"
                                    color="warning"
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    }
                />
                <CardContent>
                    {/* Unit Type Selection */}
                    <Box sx={{mb: 3}}>
                        <FormControl fullWidth>
                            <InputLabel>Unit Type</InputLabel>
                            <Select
                                value={unitType}
                                onChange={(e) => setUnitType(e.target.value)}
                                label="Unit Type"
                            >
                                <MenuItem value="long_term">
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <HomeWorkIcon/>
                                        <span>Long Term Rental</span>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="short_term">
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <AirbnbIcon/>
                                        <span>Short Term Rental (Airbnb)</span>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Basic Information */}
                    <Typography variant="h6" sx={{mb: 2, color: '#2C5283', fontWeight: 600}}>
                        Basic Information
                    </Typography>
                    <Grid container spacing={3} sx={{mb: 3}}>
                        <Grid item xs={12} sm={4}>
                            <StyledTextField
                                label="Unit Number/Name"
                                variant="outlined"
                                fullWidth
                                value={unitName}
                                onChange={(e) => setUnitName(e.target.value)}
                                placeholder="e.g. A1, 101, Ground Floor"
                                InputProps={{
                                    startAdornment: <HomeWorkIcon color="primary" sx={{mr: 1}}/>,
                                }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <StyledTextField
                                label="Initial Water Meter Reading"
                                variant="outlined"
                                type="number"
                                fullWidth
                                value={unitMeterReading}
                                onChange={(e) => setUnitMeterReading(e.target.value)}
                                placeholder="e.g. 0"
                                InputProps={{
                                    startAdornment: <Water color="info" sx={{mr: 1}}/>,
                                }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <StyledTextField
                                label="Initial Electricity Meter Reading"
                                variant="outlined"
                                type="number"
                                fullWidth
                                value={electricityUnitMeterReading}
                                onChange={(e) => setElectricityUnitMeterReading(e.target.value)}
                                placeholder="e.g. 0"
                                InputProps={{
                                    startAdornment: <ElectricBoltIcon color="warning" sx={{mr: 1}}/>,
                                }}
                                required
                            />
                        </Grid>
                    </Grid>

                    {/* Short-term Rental Settings - Show only when short_term is selected */}
                    {unitType === 'short_term' && (
                        <>
                            <Divider sx={{my: 3}}/>
                            <Typography variant="h6" sx={{mb: 2, color: '#ff5722', fontWeight: 600}}>
                                <AirbnbIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                                Airbnb Settings
                            </Typography>

                            <Alert severity="info" sx={{mb: 3}}>
                                <Typography variant="body2">
                                    <strong>Note:</strong> A unique Airbnb listing ID will be automatically generated
                                    for this unit.
                                </Typography>
                            </Alert>
                            <Grid container spacing={3} sx={{mb: 3}}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledTextField
                                        label="Nightly Rate"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                        value={nightlyRate}
                                        onChange={(e) => setNightlyRate(e.target.value)}
                                        placeholder="e.g. 50"
                                        InputProps={{
                                            startAdornment: <AttachMoneyIcon color="success" sx={{mr: 1}}/>,
                                        }}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledTextField
                                        label="Cleaning Fee"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                        value={cleaningFee}
                                        onChange={(e) => setCleaningFee(e.target.value)}
                                        placeholder="e.g. 25"
                                        InputProps={{
                                            startAdornment: <CleaningServicesIcon color="secondary" sx={{mr: 1}}/>,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledTextField
                                        label="Minimum Stay (nights)"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                        value={minimumStay}
                                        onChange={(e) => setMinimumStay(e.target.value)}
                                        placeholder="e.g. 1"
                                        InputProps={{
                                            startAdornment: <NightsStayIcon color="info" sx={{mr: 1}}/>,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <StyledTextField
                                        label="Maximum Stay (nights)"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                        value={maximumStay}
                                        onChange={(e) => setMaximumStay(e.target.value)}
                                        placeholder="e.g. 30 (optional)"
                                        InputProps={{
                                            startAdornment: <CalendarMonthIcon color="info" sx={{mr: 1}}/>,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <StyledTextField
                                        label="House Rules"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={houseRules}
                                        onChange={(e) => setHouseRules(e.target.value)}
                                        placeholder="e.g. No smoking, No pets, Quiet hours 10 PM - 8 AM"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {formError && (
                        <Alert severity="error" sx={{mt: 2}}>
                            {formError}
                        </Alert>
                    )}

                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3}}>
                        <Box sx={{display: 'flex', gap: 1}}>
                            <Button
                                variant="outlined"
                                onClick={clearForm}
                                sx={{borderRadius: 2}}
                            >
                                Clear Form
                            </Button>
                            {isEditMode && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<CancelIcon/>}
                                    onClick={cancelEdit}
                                    sx={{borderRadius: 2}}
                                >
                                    Cancel Edit
                                </Button>
                            )}
                        </Box>

                        <LoadingButton
                            variant="contained"
                            color={isEditMode ? "primary" : "success"}
                            startIcon={isEditMode ? <SaveIcon/> : <AddCircleIcon/>}
                            loading={addUnitLoading}
                            loadingPosition="start"
                            onClick={addUnit}
                            sx={{borderRadius: 2}}
                        >
                            {isEditMode ? 'Update Unit' : 'Add Unit'}
                        </LoadingButton>
                    </Box>
                </CardContent>
            </FormCard>

            {/* Units DataGrid Section */}
            <FormCard>
                <StyledCardHeader
                    title={
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between"}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <HomeWorkIcon/>
                                <span>Manage Property Units</span>
                                <Chip
                                    label={`${units.length} units`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                            <Tooltip
                                title="Enter new readings in the update fields to track water and electricity consumption. Different unit types are color-coded. Click edit to modify unit details.">
                                <IconButton size="small">
                                    <InfoIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                />
                <CardContent>
                    {/* Unit Type Legend */}
                    <Box sx={{display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Box sx={{width: 4, height: 20, backgroundColor: '#2196f3', borderRadius: 1}}/>
                            <Typography variant="caption">Long Term</Typography>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Box sx={{width: 4, height: 20, backgroundColor: '#ff5722', borderRadius: 1}}/>
                            <Typography variant="caption">Short Term</Typography>
                        </Box>
                    </Box>

                    {units.length > 0 ? (
                        <Box sx={{height: 600, width: "100%"}}>
                            <StyledDataGrid
                                rows={units}
                                columns={unitsColumns}
                                pageSize={10}
                                rowsPerPageOptions={[5, 10, 20]}
                                disableSelectionOnClick
                                getRowClassName={(params) => {
                                    const unitType = params.row.unit_type || 'long_term';
                                    return `${unitType}-row`;
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{field: "unit", sort: "asc"}],
                                    },
                                }}
                            />

                            <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    startIcon={<UpdateIcon/>}
                                    loading={updateUnitsLoading}
                                    loadingPosition="start"
                                    onClick={updateUnits}
                                    sx={{borderRadius: 2}}
                                    disabled={meterReadings.length === 0 && electricityMeterReadings.length === 0}
                                >
                                    Update Readings
                                </LoadingButton>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{py: 4, textAlign: "center"}}>
                            <AirbnbIcon sx={{fontSize: 48, color: 'text.secondary', mb: 2}}/>
                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                No units have been added to this property yet.
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Use the "Add New Unit" section above to add your first unit (long-term or short-term).
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </FormCard>
        </Container>
    );
}